import { Dispatch, Reducer, useReducer } from 'react';
import { Dayjs } from 'dayjs';
import { GenericPathsWith, QueryParams as QueryParamsApi } from '../api/apiConfig';
import { API_DATE_FORMAT } from '../constants/globals';
import { paths } from '../api/openApi/schema';
import { NonOptional, NonUndefined } from '../types/helperTypes';
import { Api } from '../types/apiSpec';

type PageChangeAction = {
  type: 'pageChange';
  payload: {
    page: number;
  };
};

type PageSizeChangeAction = {
  type: 'pageSizeChange';
  payload: {
    pageSize: number;
  };
};

type PhraseChangeAction = {
  type: 'phraseChange';
  payload: {
    phrase: string;
  };
};

type DateRangeChangeAction = {
  type: 'dateRangeChange';
  payload: {
    since: Dayjs;
    to: Dayjs;
  };
};

type SortChangeAction = {
  type: 'sortChange';
  payload: {
    sortValue: string;
  };
};

export type QueryChangeActions =
  | PageChangeAction
  | PageSizeChangeAction
  | PhraseChangeAction
  | SortChangeAction
  | DateRangeChangeAction;

export type QueryParams = {
  teamId?: string;
  pageSize: Api.PageSize;
  page: Api.Page;
  search: Api.Search;
  sortDirection: Api.SortDirection;
  since?: Api.Since;
  to?: Api.To;
  sortBy: Api.SortBy;
};

export type FilteredQueryState<T extends GenericPathsWith<paths>> = QueryParamsApi<T, 'get'>;

export type UseBackendTableReturnType<T extends GenericPathsWith<paths>> = [
  NonUndefined<NonOptional<FilteredQueryState<T>>>,
  Dispatch<QueryChangeActions>,
];

const queryReducer = <T extends GenericPathsWith<paths>>(
  prev: any,
  action: QueryChangeActions,
): FilteredQueryState<T> => {
  const { type, payload } = action;
  switch (type) {
    case 'pageChange':
      return {
        ...prev,
        page: payload.page,
      };
    case 'pageSizeChange':
      return {
        ...prev,
        pageSize: payload.pageSize,
      };
    case 'phraseChange':
      return {
        ...prev,
        search: payload.phrase,
      };
    case 'dateRangeChange':
      return {
        ...prev,
        since: payload.since.format(API_DATE_FORMAT),
        to: payload.to.format(API_DATE_FORMAT),
      };
    case 'sortChange': {
      const [sortBy, sortDirection] = payload.sortValue.split('|');

      return {
        ...prev,
        sortBy,
        sortDirection,
      };
    }
    default:
      return prev;
  }
};

const useBackendTable = <T extends GenericPathsWith<paths>>(
  initialState: FilteredQueryState<T>,
): UseBackendTableReturnType<T> => {
  const [state, dispatch] = useReducer<Reducer<FilteredQueryState<T>, QueryChangeActions>>(queryReducer, initialState);

  return [state, dispatch] as unknown as UseBackendTableReturnType<T>;
};

export default useBackendTable;
